"use client"

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import api from "../utils/api"
import { fetchAdminCompanies } from "../utils/adminCompanies"
import AdminShell from "../components/admin/AdminShell"
import Icon from "../components/Icon"
import { SkeletonAdminBody } from "../components/Skeleton"

const toDateInputValue = (value) => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return date.toISOString().slice(0, 10)
}

const formatDate = (value) => {
  if (!value) return "Not set"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Not set"
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

function AdminAgencyTrialsPage() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companies, setCompanies] = useState([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingCompany, setEditingCompany] = useState(null)
  const [form, setForm] = useState({
    enabled: true,
    startsAt: "",
    endsAt: "",
    reason: "",
    portalOverrideEnabled: false,
    portalCommissionPercentage: "",
    portalCommissionReason: ""
  })
  const [savingId, setSavingId] = useState(null)
  const navigate = useNavigate()

  const fetchCompanies = async () => {
    setCompaniesLoading(true)
    try {
      const companiesResponse = await fetchAdminCompanies()
      setCompanies(companiesResponse)
    } catch (error) {
      alert(error?.response?.data?.error || "Failed to load agencies")
    } finally {
      setCompaniesLoading(false)
    }
  }

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token")
      if (!token) return navigate("/login")

      try {
        const response = await api.get("/auth/me")
        if (response.data.role !== "A") {
          navigate("/dashboard")
          return
        }
        setUser(response.data)
        await fetchCompanies()
      } catch (error) {
        localStorage.removeItem("token")
        navigate("/login")
      } finally {
        setLoading(false)
      }
    }

    fetchUser()
  }, [navigate])

  const filteredCompanies = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return companies
    return companies.filter((company) => {
      return [
        company.companyName,
        company.telephoneNumber,
        company.headOfficeLocation,
        company.user_id?.name,
        company.user_id?.email
      ].some((value) => String(value || "").toLowerCase().includes(query))
    })
  }, [companies, search])
  const totalCompanies = companies.length
  const visibleCompanies = filteredCompanies

  const closeEditor = () => {
    setEditingId(null)
    setEditingCompany(null)
  }

  const startEditing = (company) => {
    const trial = company.freeTrial || {}
    const portalOverride = company.portalCommissionOverride || {}
    setEditingId(company._id)
    setEditingCompany(company)
    setForm({
      enabled: trial.enabled !== false,
      startsAt: toDateInputValue(trial.startsAt || new Date()),
      endsAt: toDateInputValue(trial.endsAt),
      reason: trial.reason || "",
      portalOverrideEnabled: Boolean(portalOverride.enabled),
      portalCommissionPercentage: portalOverride.percentage ?? "",
      portalCommissionReason: portalOverride.reason || ""
    })
  }

  const saveTrial = async (companyId) => {
    setSavingId(companyId)
    try {
      await api.put(`/management-company/admin/${companyId}/free-trial`, {
        enabled: form.enabled,
        startsAt: form.startsAt,
        endsAt: form.endsAt,
        reason: form.reason,
        portalCommissionOverride: {
          enabled: form.portalOverrideEnabled,
          percentage: form.portalCommissionPercentage,
          reason: form.portalCommissionReason
        }
      })
      closeEditor()
      await fetchCompanies()
    } catch (error) {
      alert(error?.response?.data?.error || "Failed to save agency settings")
    } finally {
      setSavingId(null)
    }
  }

  const disableTrial = async (company) => {
    if (!window.confirm(`Disable free trial for ${company.companyName}?`)) return
    setSavingId(company._id)
    try {
      await api.put(`/management-company/admin/${company._id}/free-trial`, {
        enabled: false,
        startsAt: null,
        endsAt: null,
        reason: "",
        portalCommissionOverride: company.portalCommissionOverride || {}
      })
      if (editingId === company._id) closeEditor()
      await fetchCompanies()
    } catch (error) {
      alert(error?.response?.data?.error || "Failed to disable agency free trial")
    } finally {
      setSavingId(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 font-sans">
        <SkeletonAdminBody rows={6} />
      </div>
    )
  }

  if (!user) return null

  return (
    <AdminShell user={user} title="Agency Management" subtitle="Super admin agency billing and commission controls" maxWidth="max-w-7xl">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{visibleCompanies.length}</span> of <span className="font-semibold text-gray-900">{totalCompanies}</span> agencies
          </div>
          <button
            onClick={fetchCompanies}
            disabled={companiesLoading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
          >
            <Icon name="arrow-clockwise" size="1rem" />
            {companiesLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <Icon name="search" size="1rem" className="text-gray-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search agencies, owners, phone, location..."
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>

          <div className="space-y-3">
            {companiesLoading ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">Loading agencies...</div>
            ) : visibleCompanies.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
                {totalCompanies > 0 ? "No agencies matched your search." : "No agencies found."}
              </div>
            ) : visibleCompanies.map((company) => {
              const trial = company.freeTrial || {}
              const portalOverride = company.portalCommissionOverride || {}
              return (
                <div key={company._id} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-gray-900">{company.companyName}</h3>
                      <p className="mt-0.5 text-xs text-gray-500">{company.user_id?.name || "No owner"} - {company.telephoneNumber || "No phone"}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${trial.active ? "bg-emerald-100 text-emerald-700" : trial.enabled ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                      {trial.active ? "Active" : trial.enabled ? "Scheduled" : "Off"}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-gray-50 p-2">
                      <div className="font-semibold text-gray-500">Properties</div>
                      <div className="mt-1 text-gray-900">{company.propertyCount || 0}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-2">
                      <div className="font-semibold text-gray-500">Period</div>
                      <div className="mt-1 text-gray-900">{formatDate(trial.startsAt)} - {formatDate(trial.endsAt)}</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-2">
                      <div className="font-semibold text-gray-500">Portal</div>
                      <div className="mt-1 text-gray-900">{portalOverride.enabled ? `${portalOverride.percentage}%` : "Global rate"}</div>
                    </div>
                  </div>

                  {trial.reason ? (
                    <p className="mt-3 rounded-lg bg-gray-50 p-2 text-xs text-gray-600">{trial.reason}</p>
                  ) : null}

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button onClick={() => startEditing(company)} className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">Manage</button>
                    {trial.enabled ? (
                      <button onClick={() => disableTrial(company)} disabled={savingId === company._id} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">Disable</button>
                    ) : (
                      <span />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {editingCompany ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Manage agency settings</h3>
                <p className="text-sm text-gray-500">{editingCompany.companyName}</p>
              </div>
              <button onClick={closeEditor} className="rounded-xl border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700">
                <Icon name="x" size="1rem" />
              </button>
            </div>

            <div className="max-h-[80vh] space-y-5 overflow-y-auto px-5 py-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Owner</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{editingCompany.user_id?.name || "No owner"}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Properties</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{editingCompany.propertyCount || 0}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Phone</div>
                  <div className="mt-1 text-sm font-medium text-gray-900">{editingCompany.telephoneNumber || "No phone"}</div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={form.enabled} onChange={(event) => setForm(prev => ({ ...prev, enabled: event.target.checked }))} />
                  Free trial enabled
                </label>
                <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm font-medium text-gray-700">
                  <input type="checkbox" checked={form.portalOverrideEnabled} onChange={(event) => setForm(prev => ({ ...prev, portalOverrideEnabled: event.target.checked }))} />
                  Custom portal commission
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Trial starts</label>
                  <input type="date" value={form.startsAt} onChange={(event) => setForm(prev => ({ ...prev, startsAt: event.target.value }))} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Trial ends</label>
                  <input type="date" value={form.endsAt} onChange={(event) => setForm(prev => ({ ...prev, endsAt: event.target.value }))} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Trial note</label>
                <input value={form.reason} onChange={(event) => setForm(prev => ({ ...prev, reason: event.target.value }))} placeholder="Reason or note" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm" />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Portal commission %</label>
                  <input type="number" min="0" max="100" step="0.01" value={form.portalCommissionPercentage} onChange={(event) => setForm(prev => ({ ...prev, portalCommissionPercentage: event.target.value }))} placeholder="Portal commission %" disabled={!form.portalOverrideEnabled} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Commission note</label>
                  <input value={form.portalCommissionReason} onChange={(event) => setForm(prev => ({ ...prev, portalCommissionReason: event.target.value }))} placeholder="Commission note" disabled={!form.portalOverrideEnabled} className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm disabled:bg-gray-100 disabled:text-gray-400" />
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
              <button onClick={closeEditor} className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => saveTrial(editingCompany._id)} disabled={savingId === editingCompany._id} className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{savingId === editingCompany._id ? "Saving..." : "Save changes"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  )
}

export default AdminAgencyTrialsPage
